import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { Grid, Button, Typography } from '@material-ui/core';
import moment from 'moment';
import { setSelectedStrategyModeAction } from 'modules/strategyModeNavigation';
import { listStrategyAction } from 'modules/strategyList';
import { RootState } from 'store';
import { FORM_MODE } from 'shared/enums';

import ActionsMenuComponent from 'components/ActionsMenu';
import { useStyles } from './styles';
import PaginatedDataGrid from 'components/PaginatedDataGrid';
import { GridCellParams, GridColumns } from '@material-ui/data-grid';
import { isLoading } from 'utils/loading';
import { Settings } from '@material-ui/icons';
import { loadStrategyAction, upadateStrategyAction } from 'modules/strategy';
import { parseStrategyResponse } from 'modules/strategy/helpers';
import { IStrategyResponse } from 'interfaces/Strategy';
import SimulationDetailPopUp from './SimulationDetailPopUp';
import RemoveContractPopUp from './RemoveContractPopUp';
import { IStrategyRegister } from 'components/StrategyRegister/interfaces';
import { gridColumnHeaderWithoutAction } from 'utils/helpers';
import {
  resetSimulationListAction,
  updateSimulationListByStrategyAction,
} from 'modules/simulationList';
import { removeStrategyAction } from 'modules/distributionContracts';
import { openAlert } from 'modules/alertHandler';
import { parseDateTimeToPTBR } from 'utils/date';
import CellObs from './CellObs';
import DialogLocal from 'components/DialogLocal';

const StrategiesList: React.FC = () => {
  const [selectedStrategy, setSelectedStrategy] = useState<IStrategyRegister>(
    null,
  );
  const [
    showSimulationDetailPopUp,
    setShowSimulationDetailPopUp,
  ] = useState<boolean>(false);
  const [
    showRemoveContractPopUp,
    setShowRemoveContractPopUp,
  ] = useState<boolean>(false);

  const dispatch = useDispatch();
  const classes = useStyles();

  const { list: simulationList } = useSelector(
    (state: RootState) => state.simulationList,
  );

  const { loading: loadingParameters, list: parametersList } = useSelector(
    (state: RootState) => state.parameters,
  );

  const {
    loading: strategyListLoading,
    count,
    list: strategyList,
  } = useSelector((state: RootState) => state.strategyList);

  const { permissions, loading: userLoading } = useSelector(
    (state: RootState) => state.user,
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      dispatch(resetSimulationListAction());
    };
  }, []);

  useEffect(() => {
    const simulatingStrategy = strategyList.some(
      (strategy) => strategy.status === 'SIMULATING',
    );

    const simulationExists = Object.values(simulationList).some((simulation) =>
      strategyList.some(
        (strategy) =>
          strategy._id === simulation.strategy &&
          simulation.status === 'PREPARING_DISTRIBUTION',
      ),
    );

    setIsSimulating(simulatingStrategy || simulationExists);
  }, [strategyList, simulationList]);

  const simulationEquals = (params: any) => {
    const id = params._id;
    const simulations = Object.values(simulationList);

    for (const simulation of simulations) {
      if (
        simulation.strategy === id &&
        simulation.status === 'PREPARING_DISTRIBUTION'
      ) {
        if (simulation.startAtDistribution) {
          const startAtDistribution = new Date(simulation.startAtDistribution);
          startAtDistribution.setTime(
            startAtDistribution.getTime() + 44 * 60 * 60 * 1000,
          );
          return startAtDistribution;
        }
        return false;
      }
    }

    return false;
  };

  const [downloadContent, setDownloadContent] = useState<any>('');
  const csvLink = useRef<any>();
  useEffect(() => {
    if (csvLink && csvLink.current) {
      csvLink.current.link.click();
    }
    setDownloadContent('');
  }, [downloadContent]);
  const handleCsv = async (id) => {
    const apiUrl = `${process.env.REACT_APP_API_BASEURL}/strategies/strategy/csv/${id}`;
    const config = {
      headers: {
        'Content-type': 'application/json',
      },
      withCredentials: true,
    };
    const csv = await axios.get(apiUrl, config).then(async (response) => {
      setDownloadContent({
        data: response.data,
        id: id,
        date: moment(new Date()).format('YYYY-MM-DD_HH:MM:SS'),
      });
    });
  };

  const loading = isLoading([
    strategyListLoading,
    userLoading,
    loadingParameters,
  ]);

  const ActionsMenu = (
    params: GridCellParams,
    {
      showAction,
      updateAction,
    }: {
      showAction: boolean;
      updateAction: boolean;
    },
  ) => {
    const items = [];
    if (showAction) {
      items.push({
        label: 'Visualizar',
        onClick: () => {
          dispatch(
            loadStrategyAction({ id: params.row.id, mode: FORM_MODE.view }),
          );
        },
      });

      const isSimulationAtLeast = [
        'SIMULATING',
        'SIMULATION_READY',
        'ACTIVE',
        'ENDING',
      ].some((status) => params.row.status === status);
      if (isSimulationAtLeast) {
        const label = ['SIMULATING', 'SIMULATION_READY'].some(
          (status) => params.row.status === status,
        )
          ? 'Detalhes da Simulação'
          : 'Ver Detalhes';

        items.push({
          label,
          onClick: async () => {
            const strategyRegister = params.row as IStrategyResponse;
            await dispatch(
              updateSimulationListByStrategyAction({
                strategyId: strategyRegister.id,
                strategyStatus: strategyRegister.status,
              }),
            );
            const strategyParsed = parseStrategyResponse(strategyRegister);
            setSelectedStrategy(strategyParsed);
            setShowSimulationDetailPopUp(true);
          },
        });
      }

      const isActive = params.row.status === 'ACTIVE';
      if (isActive) {
        items.push({
          label: 'Retirar contratos',
          onClick: () => {
            dispatch(
              openAlert({
                title: 'Remover distribuição?',
                content: `Tem certeza que deseja toda distribuição dessa estratégia?`,
                primaryButton: 'Sim',
                primaryButtonAction: removeStrategyAction({
                  strategyId: String(params.id),
                }),
                secondaryButton: 'Não',
              }),
            );
          },
        });
      }
    }
    if (updateAction) {
      if (
        params.row.status === 'DRAFT' ||
        params.row.status === 'SIMULATION_READY'
      ) {
        items.push({
          label: 'Editar',
          onClick: () => {
            dispatch(
              loadStrategyAction({ id: params.row.id, mode: FORM_MODE.edit }),
            );
          },
        });
      }
      if (
        (params.row.status === 'CREATED' ||
          params.row.status === 'SIMULATION_READY') &&
        !isSimulating
      ) {
        items.push({
          label: 'Simular',
          onClick: () => {
            const hasSimulationRunning = strategyList.some(
              (strategy) => strategy.status === 'SIMULATING',
            );
            if (hasSimulationRunning) {
              setIsDialogOpen(true);
            } else {
              const strategyRegister = params.row as IStrategyResponse;
              const strategyParsed = parseStrategyResponse(strategyRegister);

              dispatch(
                upadateStrategyAction({
                  strategy: { ...strategyParsed, status: 'SIMULATING' },
                  onSuccess: () => dispatch(listStrategyAction({})),
                }),
              );
            }
          },
        });
        items.push({
          label: 'Download csv',
          onClick: () => {
            handleCsv(params.row.id);
          },
        });
      }
    }

    return (
      <>
        <ActionsMenuComponent actionItems={items} icon={Settings} />
        {downloadContent != '' && (
          <CSVLink
            filename={`${downloadContent.id}_${downloadContent.date}`}
            data={downloadContent.data}
            ref={csvLink}
          />
        )}
      </>
    );
  };

  const actionColumn: GridColumns =
    permissions['strategy:show'] || permissions['strategy:update']
      ? [
          {
            field: 'actions',
            headerName: 'Ações',
            width: 115,
            ...gridColumnHeaderWithoutAction,
            renderCell: (params: GridCellParams) =>
              ActionsMenu(params, {
                showAction: permissions['strategy:show'],
                updateAction: permissions['strategy:update'],
              }),
          },
        ]
      : [];

  const columns: GridColumns = [
    ...actionColumn,
    {
      field: 'name',
      headerName: 'Nome',
      width: 400,
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2);
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      valueGetter: (params) => {
        const status = params.value as string;
        return parametersList.strategyStatusEnum?.[status];
      },
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2);
      },
      width: 200,
    },
    {
      field: 'obs',
      headerName: 'Obs',
      width: 500,
      renderCell: (params) => {
        const { status, id } = params.row;
        if (['ACTIVE', 'SIMULATING'].includes(status)) {
          const simulation = simulationList[id];
          const endDate = simulationEquals(params.row);
          if (endDate) {
            endDate.setHours(endDate.getHours());
            const formattedDate = `Data de Finalização: ${endDate.getDate()}/${
              endDate.getMonth() + 1
            } às ${endDate.getHours()}:${
              endDate.getMinutes() < 10 ? '0' : ''
            }${endDate.getMinutes()}`;
            return formattedDate;
          }
          return <CellObs simulation={simulation} strategyStatus={status} />;
        }
      },
      ...gridColumnHeaderWithoutAction,
    },
    {
      field: '_id',
      headerName: 'Código',
      width: 300,
    },
    {
      field: 'dateStatus',
      headerName: 'Data do Status',
      valueGetter: (params) =>
        params.value ? parseDateTimeToPTBR(params.value.toString()) : '',
      width: 170,
      filterable: false,
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2);
      },
    },
  ];

  const handleRemoveContracts = () => {
    setShowRemoveContractPopUp(true);
  };

  const handleCreateStrategy = () =>
    dispatch(setSelectedStrategyModeAction(FORM_MODE.create));

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
    >
      <DialogLocal
        title="Atenção!"
        content="Não é possível executar mais de uma simulação ao mesmo tempo! Por favor aguarde."
        open={isDialogOpen}
        hasCancelButton={false}
        isCentralized={true}
        confirmCallback={() => setIsDialogOpen(false)}
      />
      <Grid
        item
        container
        xs={12}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        className={classes.header}
      >
        <Grid item xs={7}>
          <Typography data-cy="strategies__label--title" variant="h4">
            Listagem de Estratégias
          </Typography>
        </Grid>
        <Grid item>
          {permissions['strategy:create'] && (
            <Button
              data-cy="strategies__button--remove-contracts"
              variant="outlined"
              onClick={handleRemoveContracts}
            >
              Retirar Distribuição
            </Button>
          )}
        </Grid>
        <Grid item>
          {permissions['strategy:create'] && (
            <Button
              data-cy="strategies__button--create-strategy"
              variant="contained"
              color="primary"
              onClick={handleCreateStrategy}
            >
              Criar Nova Estratégia
            </Button>
          )}
        </Grid>
      </Grid>
      {!permissions['strategy:list'] ? (
        <Typography>
          Sem permissão para visualizar a listagem de Estratégias.
        </Typography>
      ) : (
        <Grid
          data-cy="strategy-list__datagrid--strategy-list"
          item
          className={classes.gridContainer}
        >
          {showRemoveContractPopUp && (
            <RemoveContractPopUp
              show={true}
              strategy={selectedStrategy}
              handleClose={() => {
                setShowRemoveContractPopUp(false);
                setSelectedStrategy(null);
              }}
            />
          )}
          {showSimulationDetailPopUp && (
            <SimulationDetailPopUp
              show={true}
              strategy={selectedStrategy}
              handleClose={() => {
                setShowSimulationDetailPopUp(false);
                setSelectedStrategy(null);
              }}
            />
          )}

          <PaginatedDataGrid
            columns={columns}
            rows={strategyList}
            loading={loading}
            rowCount={count}
            paginationMode="client"
            paginateAction={listStrategyAction}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default StrategiesList;
